import { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { ReactNode } from 'react'

interface ScrollAnimationProps {
  children: ReactNode
  variant?: 'fade' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'scale' | 'rotate'
  delay?: number
  duration?: number
  className?: string
}

export default function ScrollAnimation({
  children,
  variant = 'fade',
  delay = 0,
  duration = 0.6,
  className = '',
}: ScrollAnimationProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })

  const variants = {
    fade: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      scale: useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]),
    },
    slideUp: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      y: useTransform(scrollYProgress, [0, 0.5, 1], [50, 0, -50]),
    },
    slideDown: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      y: useTransform(scrollYProgress, [0, 0.5, 1], [-50, 0, 50]),
    },
    slideLeft: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      x: useTransform(scrollYProgress, [0, 0.5, 1], [50, 0, -50]),
    },
    slideRight: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      x: useTransform(scrollYProgress, [0, 0.5, 1], [-50, 0, 50]),
    },
    scale: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      scale: useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]),
    },
    rotate: {
      opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]),
      rotate: useTransform(scrollYProgress, [0, 0.5, 1], [-10, 0, 10]),
    },
  }

  const selectedVariant = variants[variant]

  return (
    <motion.div
      ref={ref}
      className={className}
      style={selectedVariant}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration, delay, ease: [0.25, 1, 0.5, 1] }}
    >
      {children}
    </motion.div>
  )
}

